import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowUpRight, Minus } from 'lucide-react';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import useObjectDefinitions from './useObjectDefinitions';

// Muted, desaturated dot colors — avoids typical AI pastels
const STATUS_DOTS = [
  'bg-stone-400',
  'bg-sky-400',
  'bg-lime-500',
  'bg-rose-400',
  'bg-slate-400',
  'bg-amber-400',
  'bg-teal-400',
  'bg-zinc-500',
];

function _hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

function _getDotColor(value, choices) {
  if (choices?.length) {
    const idx = choices.indexOf(value);
    if (idx >= 0) return STATUS_DOTS[idx % STATUS_DOTS.length];
  }
  return STATUS_DOTS[_hashString(String(value)) % STATUS_DOTS.length];
}

function _getFieldRenderer(fieldType, fieldDef, isDateCol, objectDefsById) {
  // Date — quiet, secondary emphasis
  if (isDateCol || fieldType === 'date') {
    return (val) => {
      if (!val) return <span className="text-gray-300">\u2014</span>;
      const d = new Date(val);
      const now = new Date();
      const isThisYear = d.getFullYear() === now.getFullYear();
      return (
        <span className="text-gray-400 tabular-nums text-[13px] tracking-tight">
          {d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(isThisYear ? {} : { year: 'numeric' }),
          })}
        </span>
      );
    };
  }

  // Relationships — monochrome avatar, clean typography
  if (fieldType === 'object_relationship' || fieldType === 'user') {
    return (val) => {
      if (!val || typeof val !== 'object') return val || <span className="text-gray-300">\u2014</span>;
      if (!val.display_name) return <span className="text-gray-300">\u2014</span>;
      const initial = val.display_name.charAt(0).toUpperCase();
      let href = null;
      if (val.object_definition_id && val.related_object_record_id) {
        const relOd = objectDefsById?.[val.object_definition_id];
        const relSlug = relOd?.slug_plural || relOd?.slug || val.object_definition_id;
        href = `/data/${relSlug}/${val.related_object_record_id}`;
      }
      const avatar = (
        <span className="flex-shrink-0 w-[22px] h-[22px] rounded-full bg-gray-100 text-gray-500 text-[10px] font-medium flex items-center justify-center border border-gray-200/60">
          {initial}
        </span>
      );
      if (href) {
        return (
          <Link
            to={href}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-2 group/rel"
          >
            {avatar}
            <span className="truncate text-gray-600 group-hover/rel:text-gray-900 transition-colors">{val.display_name}</span>
            <ArrowUpRight className="w-3 h-3 text-gray-400 opacity-0 group-hover/rel:opacity-100 transition-opacity flex-shrink-0" />
          </Link>
        );
      }
      return (
        <span className="inline-flex items-center gap-2">
          {avatar}
          <span className="truncate text-gray-600">{val.display_name}</span>
        </span>
      );
    };
  }

  // Select — dot indicator + text, no background pill
  if (fieldType === 'select') {
    const choices = fieldDef?.options?.choices || [];
    return (val) => {
      if (!val) return <span className="text-gray-300">\u2014</span>;
      const dot = _getDotColor(val, choices);
      return (
        <span className="inline-flex items-center gap-2">
          <span className={`flex-shrink-0 w-[7px] h-[7px] rounded-full ${dot}`} />
          <span className="text-gray-600 text-[13px]">{val}</span>
        </span>
      );
    };
  }

  // Multi-select — minimal inline tags, hairline borders
  if (fieldType === 'multi_select') {
    return (val) => {
      if (!val || !Array.isArray(val) || val.length === 0) return <span className="text-gray-300">\u2014</span>;
      return (
        <span className="inline-flex items-center gap-1 flex-wrap">
          {val.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-1.5 py-[1px] rounded border border-gray-200 text-[11px] text-gray-500 font-normal leading-tight"
            >
              {tag}
            </span>
          ))}
        </span>
      );
    };
  }

  // Boolean — minimal check / dash
  if (fieldType === 'boolean') {
    return (val) => {
      if (val === true || val === 'true') {
        return (
          <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded bg-gray-900 text-white">
            <Check className="w-3 h-3" strokeWidth={2.5} />
          </span>
        );
      }
      if (val === false || val === 'false') {
        return (
          <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded border border-gray-200 text-gray-300">
            <Minus className="w-2.5 h-2.5" strokeWidth={2} />
          </span>
        );
      }
      return <span className="text-gray-300">\u2014</span>;
    };
  }

  // URL — understated link, arrow on hover
  if (fieldType === 'url') {
    return (val) => {
      if (!val) return <span className="text-gray-300">\u2014</span>;
      let display;
      try {
        display = new URL(val).hostname.replace(/^www\./, '');
      } catch {
        display = val;
      }
      return (
        <a
          href={val}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors group/url"
        >
          <span className="truncate underline decoration-gray-300 underline-offset-2 group-hover/url:decoration-gray-500 transition-colors">{display}</span>
          <ArrowUpRight className="w-3 h-3 opacity-0 group-hover/url:opacity-100 transition-opacity flex-shrink-0" />
        </a>
      );
    };
  }

  // Number — right-aligned feel via tabular nums, slightly bolder
  if (fieldType === 'number') {
    return (val) => {
      if (val === null || val === undefined || val === '') return <span className="text-gray-300">\u2014</span>;
      const num = Number(val);
      if (isNaN(num)) return val;
      return <span className="tabular-nums text-gray-700 font-medium text-[13px]">{num.toLocaleString()}</span>;
    };
  }

  // text and fallback — no custom render needed
  return null;
}

/**
 * Generic hook for view-driven CRM lists.
 * Fetches views by objectDefinitionId and entries via /views/:id/entries.
 *
 * @param {string} objectDefinitionId - The ObjectDefinition ID
 */
export default function useObjectView(objectDefinitionId) {
  const { getCachedData, setCachedData } = useDataCache();
  const cacheKey = `crm-${objectDefinitionId}-entries`;
  const cached = getCachedData(cacheKey);
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [entries, setEntries] = useState(cached || []);
  const [loading, setLoading] = useState(!cached);
  const skipNextLoadingRef = useRef(!!cached);
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterMode, setFilterMode] = useState('create');
  const [editingView, setEditingView] = useState(null);
  const { showSuccess, showError } = useToast();
  const { organization } = useAuth();
  const { objectDefinitions } = useObjectDefinitions();

  // Build a lookup map from objectDefinitionId -> objectDefinition (for relationship links)
  const objectDefsById = useMemo(() => {
    const map = {};
    for (const od of objectDefinitions) {
      map[od.id] = od;
    }
    return map;
  }, [objectDefinitions]);

  // The active view object (derived from views + activeViewId)
  const activeView = useMemo(
    () => views.find((v) => v.id === activeViewId) || null,
    [views, activeViewId]
  );

  // Build a field lookup from availableFields (includes type, options, etc.)
  const fieldDefMap = useMemo(() => {
    const map = {};
    for (const f of availableFields) {
      map[f.id] = f;
    }
    return map;
  }, [availableFields]);

  // Convert view columns to AdvancedDataTable column format
  const tableColumns = useMemo(() => {
    if (!activeView || !activeView.columns?.length) return [];
    return activeView.columns
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((col) => {
        const key = col.field_definition_id || col.system_field;
        const isDateCol = col.system_field === 'created_at' || col.system_field === 'updated_at';
        const fd = col.field_definition_id ? fieldDefMap[col.field_definition_id] : null;
        const fieldType = fd?.field_type;

        const render = _getFieldRenderer(fieldType, fd, isDateCol, objectDefsById);

        return {
          key,
          label: col.label,
          width: col.width || 150,
          sortable: true,
          ...(render && { render }),
        };
      });
  }, [activeView, fieldDefMap, objectDefsById]);

  // Fetch views and fields for this object definition
  const fetchViews = useCallback(async () => {
    if (!objectDefinitionId) return;
    try {
      // Fetch views
      const response = await apiRequest(`/crm/views/?object_definition_id=${objectDefinitionId}`);
      if (response.ok) {
        const data = await response.json();
        const viewList = Array.isArray(data) ? data : [];
        setViews(viewList);
        if (!activeViewId && viewList.length > 0) {
          setActiveViewId(viewList[0].id);
        }
      }

      // Fetch available fields for column picker
      const fieldsRes = await apiRequest(`/crm/fields/?object_definition_id=${objectDefinitionId}`);
      if (fieldsRes.ok) {
        const fields = await fieldsRes.json();
        setAvailableFields(Array.isArray(fields) ? fields : []);
      }
    } catch (error) {
      console.error('Error fetching views:', error);
    }
  }, [objectDefinitionId, activeViewId]);

  // Kanban: derive columns and grouped entries
  const isKanban = activeView?.view_type === 'kanban';
  const groupByFieldId = activeView?.group_by_field_id;

  const kanbanColumns = useMemo(() => {
    if (!isKanban || !groupByFieldId) return [];
    const fd = fieldDefMap[groupByFieldId];
    const choices = fd?.options?.choices || [];
    const cols = choices.map((c) => ({ value: c, label: c }));
    cols.push({ value: '__uncategorized__', label: 'Uncategorized' });
    return cols;
  }, [isKanban, groupByFieldId, fieldDefMap]);

  const kanbanGroupedEntries = useMemo(() => {
    if (!isKanban || !groupByFieldId) return {};
    const grouped = {};
    for (const col of kanbanColumns) {
      grouped[col.value] = [];
    }
    for (const entry of entries) {
      const val = entry[groupByFieldId];
      const key = val || '__uncategorized__';
      if (grouped[key]) {
        grouped[key].push(entry);
      } else {
        // Value not in choices — put in uncategorized
        grouped['__uncategorized__'] = grouped['__uncategorized__'] || [];
        grouped['__uncategorized__'].push(entry);
      }
    }
    return grouped;
  }, [isKanban, groupByFieldId, kanbanColumns, entries]);

  // Kanban: display fields derived from view columns
  const kanbanDisplayFields = useMemo(() => {
    if (!activeView?.columns?.length) return [];
    return activeView.columns
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((col) => ({
        field_definition_id: col.field_definition_id,
        system_field: col.system_field,
        key: col.field_definition_id || col.system_field,
        label: col.label,
      }));
  }, [activeView]);

  // Fetch entries for the active view
  const fetchEntries = useCallback(async () => {
    if (!activeViewId) {
      setEntries([]);
      setTotalItems(0);
      return;
    }
    try {
      if (!skipNextLoadingRef.current) {
        setLoading(true);
      }
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);

      // Kanban: fetch all entries (no pagination), Table: use pagination
      if (isKanban) {
        params.append('skip', 0);
        params.append('limit', 500);
      } else {
        params.append('skip', (currentPage - 1) * pageSize);
        params.append('limit', pageSize);
      }

      const response = await apiRequest(`/crm/views/${activeViewId}/entries?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries || []);
        setTotalItems(data.total || 0);
        setCachedData(cacheKey, data.entries || []);
      } else {
        setEntries([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      setEntries([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
      skipNextLoadingRef.current = false;
    }
  }, [activeViewId, query, sortBy, sortOrder, currentPage, pageSize, cacheKey, setCachedData, isKanban]);

  // Initial load: fetch views
  useEffect(() => {
    if (organization) {
      fetchViews();
    }
  }, [organization, fetchViews]);

  // Fetch entries whenever the active view or query params change
  useEffect(() => {
    if (organization && activeViewId) {
      fetchEntries();
    }
  }, [organization, activeViewId, fetchEntries]);

  const handleSort = (key, order) => {
    setSortBy(key);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleSelectView = (viewId) => {
    setActiveViewId(viewId || (views.length > 0 ? views[0].id : null));
    setCurrentPage(1);
    setSortBy(null);
    setSortOrder(null);
  };

  const handleMoveRecord = useCallback(async (recordId, newValue) => {
    if (!activeView || !groupByFieldId) return;
    const fd = fieldDefMap[groupByFieldId];
    if (!fd) return;

    // Optimistic update
    setEntries((prev) =>
      prev.map((e) =>
        e.id === recordId ? { ...e, [groupByFieldId]: newValue } : e
      )
    );

    try {
      const response = await apiRequest(
        `/crm/objects/${activeView.object_definition_id}/records/${recordId}`,
        {
          method: 'PUT',
          body: JSON.stringify({ [fd.slug]: newValue }),
        }
      );
      if (!response.ok) {
        showError('Failed to move record');
        fetchEntries(); // revert
      }
    } catch (error) {
      console.error('Error moving record:', error);
      showError('Failed to move record');
      fetchEntries(); // revert
    }
  }, [activeView, groupByFieldId, fieldDefMap, fetchEntries, showError]);

  const handleCreateView = () => {
    setFilterMode('create');
    setEditingView(null);
    setShowFilterModal(true);
  };

  const handleEditView = (view) => {
    setFilterMode('edit');
    setEditingView(view);
    setShowFilterModal(true);
  };

  const handleSaveView = async (viewData) => {
    try {
      if (filterMode === 'create') {
        if (!objectDefinitionId) {
          showError('No object definition found');
          return;
        }
        const objDefId = objectDefinitionId;
        const response = await apiRequest('/crm/views/', {
          method: 'POST',
          body: JSON.stringify({
            object_definition_id: objDefId,
            name: viewData.name,
            columns: viewData.columns || (activeView?.columns || []).map((c) => ({
              field_definition_id: c.field_definition_id,
              system_field: c.system_field,
              label: c.label,
              sort_order: c.sort_order,
              width: c.width,
            })),
            filters: viewData.filters || {},
            visibility: 'public',
            view_type: viewData.view_type || 'table',
            group_by_field_id: viewData.group_by_field_id || null,
          }),
        });
        if (response.ok) {
          const newView = await response.json();
          setViews((prev) => [...prev, newView]);
          setActiveViewId(newView.id);
          setCurrentPage(1);
          showSuccess('View created successfully!');
        } else {
          showError('Failed to create view');
        }
      } else if (filterMode === 'edit' && editingView) {
        const body = { name: viewData.name };
        if (viewData.filters !== undefined) body.filters = viewData.filters;
        if (viewData.columns !== undefined) body.columns = viewData.columns;
        if (viewData.view_type !== undefined) body.view_type = viewData.view_type;
        if (viewData.group_by_field_id !== undefined) body.group_by_field_id = viewData.group_by_field_id;
        const response = await apiRequest(`/crm/views/${editingView.id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        if (response.ok) {
          const updatedView = await response.json();
          setViews((prev) => prev.map((v) => (v.id === updatedView.id ? updatedView : v)));
          showSuccess('View updated successfully!');
        } else {
          showError('Failed to update view');
        }
      }
      setShowFilterModal(false);
    } catch (error) {
      console.error('Error saving view:', error);
      showError('Failed to save view');
    }
  };

  // Persist column width after resize — fire-and-forget, no optimistic view update
  // (the table already shows the new width locally via its own state)
  const handleColumnResize = useCallback(async (colKey, newWidth) => {
    if (!activeView?.columns) return;
    const columnsPayload = activeView.columns.map((c, i) => {
      const key = c.field_definition_id || c.system_field;
      return {
        field_definition_id: c.field_definition_id,
        system_field: c.system_field,
        label: c.label,
        sort_order: i,
        width: key === colKey ? Math.round(newWidth) : c.width,
      };
    });
    try {
      const response = await apiRequest(`/crm/views/${activeView.id}`, {
        method: 'PUT',
        body: JSON.stringify({ columns: columnsPayload }),
      });
      if (response.ok) {
        const updated = await response.json();
        setViews((prev) => prev.map((v) => v.id === updated.id ? updated : v));
      }
    } catch (error) {
      console.error('Error saving column width:', error);
    }
  }, [activeView]);

  // Inline update: auto-save column changes from toolbar
  const handleInlineColumnsChange = useCallback(async (newColumns) => {
    if (!activeView) return;
    const columnsPayload = newColumns.map((c, i) => ({
      field_definition_id: c.field_definition_id,
      system_field: c.system_field,
      label: c.label,
      sort_order: i,
      width: c.width,
    }));
    // Optimistic update
    setViews((prev) =>
      prev.map((v) => v.id === activeView.id ? { ...v, columns: columnsPayload } : v)
    );
    try {
      const response = await apiRequest(`/crm/views/${activeView.id}`, {
        method: 'PUT',
        body: JSON.stringify({ columns: columnsPayload }),
      });
      if (response.ok) {
        const updated = await response.json();
        setViews((prev) => prev.map((v) => v.id === updated.id ? updated : v));
        fetchEntries();
      }
    } catch (error) {
      console.error('Error updating columns:', error);
    }
  }, [activeView, fetchEntries]);

  // Inline update: auto-save filter changes from toolbar
  const handleInlineFiltersChange = useCallback(async (newFilters) => {
    if (!activeView) return;
    // Optimistic update
    setViews((prev) =>
      prev.map((v) => v.id === activeView.id ? { ...v, filters: newFilters } : v)
    );
    try {
      const response = await apiRequest(`/crm/views/${activeView.id}`, {
        method: 'PUT',
        body: JSON.stringify({ filters: newFilters }),
      });
      if (response.ok) {
        const updated = await response.json();
        setViews((prev) => prev.map((v) => v.id === updated.id ? updated : v));
        fetchEntries();
      }
    } catch (error) {
      console.error('Error updating filters:', error);
    }
  }, [activeView, fetchEntries]);

  // Duplicate the active view
  const handleDuplicateView = useCallback(async () => {
    if (!activeView) return;
    try {
      const response = await apiRequest('/crm/views/', {
        method: 'POST',
        body: JSON.stringify({
          object_definition_id: activeView.object_definition?.id || objectDefinitionId,
          name: `${activeView.name} (Copy)`,
          columns: (activeView.columns || []).map((c) => ({
            field_definition_id: c.field_definition_id,
            system_field: c.system_field,
            label: c.label,
            sort_order: c.sort_order,
            width: c.width,
          })),
          filters: activeView.filters || {},
          visibility: activeView.visibility || 'public',
          view_type: activeView.view_type || 'table',
          group_by_field_id: activeView.group_by_field_id || null,
        }),
      });
      if (response.ok) {
        const newView = await response.json();
        setViews((prev) => [...prev, newView]);
        setActiveViewId(newView.id);
        showSuccess('View duplicated');
      } else {
        showError('Failed to duplicate view');
      }
    } catch (error) {
      console.error('Error duplicating view:', error);
      showError('Failed to duplicate view');
    }
  }, [activeView, objectDefinitionId, showSuccess, showError]);

  // Reorder views (drag-and-drop tabs)
  const handleReorderViews = useCallback(async (newViews) => {
    setViews(newViews);
    try {
      await apiRequest('/crm/views/reorder', {
        method: 'PUT',
        body: JSON.stringify({ order: newViews.map((v) => v.id) }),
      });
    } catch (error) {
      console.error('Error reordering views:', error);
    }
  }, []);

  // Toggle pin/unpin a view
  const handleTogglePin = useCallback(async (viewId) => {
    const view = views.find((v) => v.id === viewId);
    if (!view) return;
    const newPinned = !view.is_pinned;
    // Optimistic update
    setViews((prev) =>
      prev.map((v) => v.id === viewId ? { ...v, is_pinned: newPinned } : v)
    );
    try {
      const response = await apiRequest(`/crm/views/${viewId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_pinned: newPinned }),
      });
      if (response.ok) {
        const updated = await response.json();
        setViews((prev) => prev.map((v) => v.id === updated.id ? updated : v));
      }
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  }, [views]);

  const handleDeleteView = async (viewId) => {
    try {
      const response = await apiRequest(`/crm/views/${viewId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        const remaining = views.filter((v) => v.id !== viewId);
        setViews(remaining);
        if (activeViewId === viewId) {
          setActiveViewId(remaining.length > 0 ? remaining[0].id : null);
        }
        showSuccess('View deleted successfully!');
      } else {
        showError('Failed to delete view');
      }
    } catch (error) {
      console.error('Error deleting view:', error);
      showError('Failed to delete view');
    }
  };

  return {
    views,
    activeView,
    activeViewId,
    entries,
    tableColumns,
    loading,
    query,
    setQuery,
    currentPage,
    setCurrentPage,
    pageSize,
    totalItems,
    sortBy,
    sortOrder,
    showFilterModal,
    setShowFilterModal,
    filterMode,
    editingView,
    availableFields,
    fieldDefMap,
    fetchEntries,
    handleSort,
    handlePageSizeChange,
    handleSelectView,
    handleCreateView,
    handleEditView,
    handleSaveView,
    handleDeleteView,
    handleInlineColumnsChange,
    handleInlineFiltersChange,
    handleDuplicateView,
    handleColumnResize,
    handleReorderViews,
    handleTogglePin,
    // Kanban
    isKanban,
    kanbanColumns,
    kanbanGroupedEntries,
    kanbanDisplayFields,
    handleMoveRecord,
  };
}
